import {
  infiniteQueryOptions,
  useMutation,
  useQueryClient,
  useSuspenseInfiniteQuery,
} from '@tanstack/react-query';
import type {
  InvitePayload,
  Referral,
  ReferralActionParams,
  ReferralsPageResponse,
} from '@/pages/ReferralsTab/types.ts';
import { HttpService } from '@/services/HttpService.ts';

const PAGE_SIZE = 20;

const getReferrals = async (accountantExternalId: string, page: number, search?: string) => {
  const { data } = await HttpService.get<ReferralsPageResponse>(
    `/v1/accountant/${encodeURIComponent(accountantExternalId)}/referrals`,
    { params: { page, size: PAGE_SIZE, ...(search && { search }) } }
  );
  return data;
};

export const referralsQueryKey = (accountantExternalId: string, search?: string) =>
  ['referrals', accountantExternalId, search ?? ''] as const;

export const referralsInfiniteQueryOptions = (accountantExternalId: string, search?: string) =>
  infiniteQueryOptions({
    queryKey: referralsQueryKey(accountantExternalId, search),
    queryFn: ({ pageParam }) => getReferrals(accountantExternalId, pageParam, search),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.number + 1),
  });

export const useReferrals = (accountantExternalId: string, search?: string) => {
  return useSuspenseInfiniteQuery(referralsInfiniteQueryOptions(accountantExternalId, search));
};

const sendInvite = async (accountantExternalId: string, payload: InvitePayload) => {
  const { data } = await HttpService.post<Referral>(
    `/v1/accountant/${encodeURIComponent(accountantExternalId)}/referrals/invite`,
    payload
  );
  return data;
};

export const useSendInvite = (accountantExternalId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: InvitePayload) => sendInvite(accountantExternalId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['referrals', accountantExternalId],
      });
    },
    meta: { handlesError: true },
  });
};

const getInviteUrl = async ({ accountantExternalId, referralExternalId }: ReferralActionParams) => {
  const { data } = await HttpService.get<{ inviteUrl: string }>(
    `/v1/accountant/${encodeURIComponent(accountantExternalId)}/referrals/${encodeURIComponent(referralExternalId)}/invite-url`
  );
  return data;
};

const resendInvitation = async ({
  accountantExternalId,
  referralExternalId,
}: ReferralActionParams) => {
  const { data } = await HttpService.post<Referral>(
    `/v1/accountant/${encodeURIComponent(accountantExternalId)}/referrals/${encodeURIComponent(referralExternalId)}/resend`
  );
  return data;
};

const cancelInvitation = async ({
  accountantExternalId,
  referralExternalId,
}: ReferralActionParams) => {
  const { data } = await HttpService.post<Referral>(
    `/v1/accountant/${encodeURIComponent(accountantExternalId)}/referrals/${encodeURIComponent(referralExternalId)}/cancel`
  );
  return data;
};

export const useCopyInviteLink = () => {
  return useMutation({
    mutationFn: async (params: ReferralActionParams) => {
      const { inviteUrl } = await getInviteUrl(params);
      await navigator.clipboard.writeText(inviteUrl);
    },
  });
};

export const useResendInvitation = (accountantExternalId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resendInvitation,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['referrals', accountantExternalId],
      });
    },
  });
};

export const useCancelInvitation = (accountantExternalId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelInvitation,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['referrals', accountantExternalId],
      });
    },
  });
};
